/**
 * Status Management Card Component
 *
 * Displays current quote status and allows changing status
 */

import { DownOutlined } from '@ant-design/icons';
import { Button, Card, Divider, Dropdown, Tag } from 'antd';
import React from 'react';
import { IoCalendarOutline, IoSwapHorizontalOutline } from 'react-icons/io5';
import { QUOTE_STATUS_COLORS } from '../../constants';
import { QuoteStatus } from '../../types';
import { getAvailableActions } from '../../utils';

interface StatusManagementCardProps {
  currentStatusCode: number;
  statusName: string
  onStatusSelect: (status: number) => void;
}

const STATUS_OPTIONS = [
  { value: QuoteStatus.CONVERTED, label: 'Converted', menuLabel: 'Place Order' },
  { value: QuoteStatus.EXPIRED, label: 'Expired', menuLabel: 'Mark as Expired' },
];

export const StatusManagementCard: React.FC<StatusManagementCardProps> = ({
  currentStatusCode,
  onStatusSelect,
  statusName
}) => {
  const availableActions = getAvailableActions(currentStatusCode);

  const getStatusIcon = (status: number) => {
    switch (status) {
      // case QuoteStatus.REVISED:
      //   return <IoBriefcaseOutline className="text-base" />;
      case QuoteStatus.CONVERTED:
        return <IoSwapHorizontalOutline className="text-base" />;
      case QuoteStatus.EXPIRED:
        return <IoCalendarOutline className="text-base" />;
      default:
        return null;
    }
  };

  // Filter options based on available actions
  const getFilteredOptions = () => {
    return STATUS_OPTIONS.filter(option => {
      switch (option.value) {
        // case QuoteStatus.REVISED:
        //   return availableActions.canMarkAsRevised;
        case QuoteStatus.CONVERTED:
          return availableActions.canConvertToOrder;
        case QuoteStatus.EXPIRED:
          return availableActions.canMarkAsExpired;
        default:
          return false;
      }
    });
  };

  const filteredOptions = getFilteredOptions();

  // If no actions available, show message
  if (filteredOptions.length === 0) {
    return (
      <Card className="mb-6">
        <h3 className="text-lg text-left font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <IoSwapHorizontalOutline className="" />
          Status Management
        </h3>
        <Divider className="mt-0 mb-4" />

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 uppercase text-sm font-medium">CURRENT STATUS:</span>
            <Tag
              color={QUOTE_STATUS_COLORS[currentStatusCode] || 'default'}
              style={{
                borderRadius: '12px',
                padding: '4px 12px',
                border: 'none',
              }}
            >
              {statusName}
            </Tag>
          </div>
        </div>
      </Card>
    );
  }

  const statusMenuItems = filteredOptions.map((option) => ({
    key: option.value,
    icon: getStatusIcon(option.value),
    label: (
      <span className="flex items-center justify-between w-full">
        <span>{option.menuLabel}</span>
        {option.value === currentStatusCode && (
          <Tag color={QUOTE_STATUS_COLORS[option.value] || 'default'} className="text-xs ml-2">Current</Tag>
        )}
      </span>
    ),
    disabled: option.value === currentStatusCode,
    onClick: () => onStatusSelect(option.value),
  }));

  const menuItemsWithDivider = [...statusMenuItems];

  return (
    <Card className="mb-6">
      <h3 className="text-lg text-left font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <IoSwapHorizontalOutline className="" />
        Status Management
      </h3>
      <Divider className="mt-0 mb-4" />

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-600 uppercase text-sm font-medium">CURRENT STATUS:</span>
          {/* <Tag
            color={currentStatusOption?.color}
            className="px-4 py-2 text-sm rounded-full"
          >
            {statusName}
          </Tag> */}
          <Tag
            color={QUOTE_STATUS_COLORS[currentStatusCode] || 'default'}
            style={{
              borderRadius: '12px',
              padding: '4px 12px',
              border: 'none',
            }}
          >
            {statusName}
          </Tag>
        </div>
      </div>

      <div>
        <label className="block text-left text-sm font-medium text-gray-700 mb-2">
          Change Status:
        </label>
        <Dropdown
          menu={{ items: menuItemsWithDivider }}
          trigger={['click']}
          placement="bottomLeft"
        >
          <Button
            size="large"
            className="w-full"
          >
            <div className="flex items-center justify-between w-full">
              <span>{statusName}</span>
              <div className="flex items-center gap-2">
                <Tag color={QUOTE_STATUS_COLORS[currentStatusCode] || 'default'} className="text-xs">Current</Tag>
                <DownOutlined />
              </div>
            </div>
          </Button>
        </Dropdown>
      </div>
    </Card>
  );
};

export default StatusManagementCard;
